import { AlertCircle, ArrowLeft, Home, LucideIcon } from 'lucide-react';
import { JSX } from 'react';

import { Link, Button } from '@/components/design-system';
import {
  SPACE_Y_SCALE,
  COLOR_SCALE,
  TYPE_SCALE,
  FONT_WEIGHTS,
  RADIUS_SCALE,
  PADDING_X,
  PADDING_Y,
  SURFACE_PADDING,
  SIZE_SCALE,
  MARGIN_BOTTOM,
  MARGIN_X,
  MARGIN_TOP,
  MARGIN_RIGHT,
} from '@/components/design-system/tokens';
import { useTranslation } from '@/hooks/useTranslation';
import { createPageUrl } from '@/utils';

interface ErrorStateProps {
  error?: string | Error;
  onRetry?: () => void;
  variant?: 'fullscreen' | 'inline' | 'compact';
  icon?: LucideIcon;
  title?: string;
  message?: string;
  backUrl?: string;
  showRefresh?: boolean;
  showHome?: boolean;
  retryText?: string;
}

/**
 * Unified ErrorState component that handles different error display patterns
 */
// Helper function to extract error message
const getErrorMessage = (
  error: string | Error | undefined,
  t: (ns: string, key: string, fallback: string) => string
): string => {
  if (typeof error === 'object' && 'message' in error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return t('actions', 'unexpected_error', 'An unexpected error occurred');
};

// Helper function to get default content configuration
const getDefaultContent = (
  variant: string,
  title: string | undefined,
  message: string | undefined,
  errorMessage: string,
  t: (ns: string, key: string, fallback: string) => string
) => {
  const baseConfigs = {
    inline: {
      title: title ?? t('common', 'error_occurred', 'An Error Occurred'),
      message: message ?? errorMessage,
      iconSize: 'w-8 h-8',
      containerClasses: `text-center ${PADDING_Y['8']}`,
      iconBgClasses: COLOR_SCALE['error-bg'],
      iconColorClasses: COLOR_SCALE.error,
    },
    compact: {
      title: title ?? t('common', 'error', 'Error'),
      message: message ?? errorMessage,
      iconSize: 'w-6 h-6',
      containerClasses: `text-center ${PADDING_Y['4']}`,
      iconBgClasses: COLOR_SCALE['error-bg'],
      iconColorClasses: COLOR_SCALE.error,
    },
    fullscreen: {
      title: title ?? t('common', 'error_occurred', 'An Error Occurred'),
      message: message ?? t('common', 'error_try_again', 'Something went wrong. Please try again.'),
      iconSize: 'w-16 h-16',
      containerClasses: `${SIZE_SCALE.screen} ${COLOR_SCALE['surface-muted']} ${PADDING_Y['12']} flex items-center justify-center`,
      iconBgClasses: COLOR_SCALE['surface-subtle'],
      iconColorClasses: COLOR_SCALE['foreground-subtle'],
    },
  };

  return baseConfigs[variant as keyof typeof baseConfigs];
};

// Helper function to create retry handler
const createRetryHandler = (onRetry: (() => void) | undefined, showRefresh: boolean) => {
  return () => {
    if (onRetry) {
      onRetry();
    } else if (showRefresh) {
      window.location.reload();
    }
  };
};

// Type for content configuration
type ContentConfig = {
  title: string;
  message: string;
  iconSize: string;
  containerClasses: string;
  iconBgClasses: string;
  iconColorClasses: string;
};

// Helper function to render the error state UI
const renderErrorStateUI = (
  content: ContentConfig,
  variant: string,
  onRetry: (() => void) | undefined,
  showRefresh: boolean,
  backUrl: string | undefined,
  showHome: boolean,
  retryText: string | undefined,
  IconComponent: LucideIcon,
  t: (ns: string, key: string, fallback: string) => string
): JSX.Element => {
  return (
    <div className={content.containerClasses}>
      <div className={`text-center max-w-md mx-auto ${PADDING_X['4']}`}>
        {/* Icon */}
        <div
          className={`${SIZE_SCALE.w32} ${SIZE_SCALE['32']} ${MARGIN_X.auto} ${content.iconBgClasses} ${RADIUS_SCALE.full} flex items-center justify-center ${MARGIN_BOTTOM['6']}`}
        >
          <IconComponent className={`${content.iconSize} ${content.iconColorClasses}`} />
        </div>

        {/* Title */}
        <h1
          className={`${FONT_WEIGHTS.bold} ${COLOR_SCALE.foreground} ${MARGIN_BOTTOM['4']} ${
            variant === 'compact' ? TYPE_SCALE.lg : TYPE_SCALE['2xl']
          }`}
        >
          {content.title}
        </h1>

        {/* Message */}
        <p className={`${COLOR_SCALE['foreground-subtle']} ${MARGIN_BOTTOM['8']}`}>
          {content.message}
        </p>

        {/* Actions */}
        <div className={SPACE_Y_SCALE['4']}>
          {onRetry || showRefresh ? (
            <Button
              onClick={createRetryHandler(onRetry, showRefresh)}
              variant="primary"
              className={`${PADDING_X['6']} ${PADDING_Y['3']} ${RADIUS_SCALE.xl}`}
            >
              {retryText ?? t('common', 'try_again', 'Try Again')}
            </Button>
          ) : null}

          {variant === 'fullscreen' && backUrl && (
            <div className={MARGIN_TOP['4']}>
              <Link to={backUrl}>
                <Button variant="outline" className={`${SURFACE_PADDING['6']}`}>
                  <ArrowLeft
                    className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']} ${MARGIN_RIGHT['2']}`}
                  />
                  {t('common', 'go_back', 'Go Back')}
                </Button>
              </Link>
            </div>
          )}

          {showHome && (
            <div className={MARGIN_TOP['4']}>
              <Link to={createPageUrl('Home')}>
                <Button variant="outline" className={`${SURFACE_PADDING['6']}`}>
                  <Home className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']} ${MARGIN_RIGHT['2']}`} />
                  {t('common', 'back_to_home', 'Back to Home')}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export function ErrorState({
  error,
  onRetry,
  variant = 'fullscreen',
  icon: IconComponent = AlertCircle,
  title,
  message,
  backUrl,
  showRefresh = true,
  showHome = false,
  retryText,
}: ErrorStateProps): JSX.Element {
  const { t } = useTranslation();

  const errorMessage = getErrorMessage(error, t);
  const content = getDefaultContent(variant, title, message, errorMessage, t);

  return renderErrorStateUI(
    content,
    variant,
    onRetry,
    showRefresh,
    backUrl,
    showHome,
    retryText,
    IconComponent,
    t
  );
}

// Specialized variants for common use cases
export function FullscreenErrorState(props: Omit<ErrorStateProps, 'variant'>): JSX.Element {
  return <ErrorState variant="fullscreen" {...props} />;
}

export function InlineErrorState(props: Omit<ErrorStateProps, 'variant'>): JSX.Element {
  return <ErrorState variant="inline" {...props} />;
}

export function CompactErrorState(props: Omit<ErrorStateProps, 'variant'>): JSX.Element {
  return <ErrorState variant="compact" {...props} />;
}
